import { useState } from 'react'
import {
  FileText,
  Upload,
  Search,
  MoreVertical,
  Grid,
  List,
  CheckCircle2,
  Clock,
  AlertCircle,
  Download,
  Trash2,
  Eye,
  RefreshCw,
  Filter,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

interface Manual {
  id: string
  name: string
  type: string
  aircraft: string
  revision: string
  pages: number
  sections: number
  chunks: number
  size: string
  status: 'ready' | 'processing' | 'error'
  uploadedBy: string
  uploadedAt: Date
  lastIndexed?: Date
}

const sampleManuals: Manual[] = [
  {
    id: '1',
    name: 'B737-800 Aircraft Maintenance Manual',
    type: 'AMM',
    aircraft: 'B737-800',
    revision: 'Rev 45',
    pages: 2847,
    sections: 78,
    chunks: 12453,
    size: '156 MB',
    status: 'ready',
    uploadedBy: 'John Martinez',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5),
    lastIndexed: new Date(Date.now() - 1000 * 60 * 60 * 2),
  },
  {
    id: '2',
    name: 'A320 Component Maintenance Manual',
    type: 'CMM',
    aircraft: 'A320',
    revision: 'Rev 38',
    pages: 1523,
    sections: 45,
    chunks: 6782,
    size: '89 MB',
    status: 'processing',
    uploadedBy: 'Sarah Chen',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 15),
  },
  {
    id: '3',
    name: 'B777-300ER Service Bulletins Collection',
    type: 'SB',
    aircraft: 'B777-300ER',
    revision: '2024-Q4',
    pages: 892,
    sections: 156,
    chunks: 4521,
    size: '67 MB',
    status: 'ready',
    uploadedBy: 'Mike Thompson',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2),
    lastIndexed: new Date(Date.now() - 1000 * 60 * 60 * 24),
  },
  {
    id: '4',
    name: 'EMB-145 Aircraft Maintenance Manual',
    type: 'AMM',
    aircraft: 'EMB-145',
    revision: 'Rev 22',
    pages: 0,
    sections: 0,
    chunks: 0,
    size: '0 MB',
    status: 'error',
    uploadedBy: 'Lisa Wong',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 60),
  },
  {
    id: '5',
    name: 'B787-9 Fault Isolation Manual',
    type: 'FIM',
    aircraft: 'B787-9',
    revision: 'Rev 12',
    pages: 3421,
    sections: 92,
    chunks: 15234,
    size: '201 MB',
    status: 'ready',
    uploadedBy: 'John Martinez',
    uploadedAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10),
    lastIndexed: new Date(Date.now() - 1000 * 60 * 60 * 48),
  },
]

export function ManualsPage() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  const filteredManuals = sampleManuals.filter(
    (manual) =>
      manual.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      manual.aircraft.toLowerCase().includes(searchQuery.toLowerCase()) ||
      manual.type.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />
      case 'processing':
        return <Clock className="h-4 w-4 text-yellow-500 animate-pulse" />
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />
      default:
        return null
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready':
        return <Badge variant="success">Ready</Badge>
      case 'processing':
        return <Badge variant="warning">Processing</Badge>
      case 'error':
        return <Badge variant="destructive">Error</Badge>
      default:
        return null
    }
  }

  const handleFileSelect = (file: File) => {
    if (file.type !== 'application/pdf') {
      setUploadError('Only PDF files are supported')
      return
    }
    if (file.size > 200 * 1024 * 1024) {
      setUploadError('File size must be less than 200 MB')
      return
    }
    setSelectedFile(file)
    setUploadError(null)
  }

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file to upload')
      return
    }

    setUploading(true)
    setUploadError(null)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)

      const response = await fetch('/api/documents/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('voltaire_token')}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Upload failed')
      }

      setUploadSuccess(true)
      setTimeout(() => {
        setShowUploadModal(false)
        setSelectedFile(null)
        setUploadSuccess(false)
      }, 2000)
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleCloseModal = () => {
    setShowUploadModal(false)
    setSelectedFile(null)
    setUploadError(null)
    setUploadSuccess(false)
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manuals</h1>
          <p className="text-gray-600">
            {sampleManuals.length} manuals indexed • {sampleManuals.reduce((acc, m) => acc + m.chunks, 0).toLocaleString()} chunks
          </p>
        </div>
        <Button onClick={() => setShowUploadModal(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Upload Manual
        </Button>
      </div>

      {/* Filters & Search */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search manuals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Filter className="mr-2 h-4 w-4" />
            Filter
          </Button>
          <div className="flex rounded-md border">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-r-none"
              onClick={() => setViewMode('grid')}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              className="rounded-l-none"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Manuals Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredManuals.map((manual) => (
            <Card key={manual.id} className="group hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-navy-900/10">
                    <FileText className="h-6 w-6 text-navy-900" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Re-index
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="mt-4">
                  <h3 className="font-semibold text-gray-900 line-clamp-2">{manual.name}</h3>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant="outline">{manual.type}</Badge>
                    <Badge variant="secondary">{manual.aircraft}</Badge>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                  <span>{manual.pages.toLocaleString()} pages</span>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(manual.status)}
                    <span className="capitalize">{manual.status}</span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t text-xs text-gray-400">
                  {manual.revision} • {formatDate(manual.uploadedAt)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border bg-white overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Manual
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Aircraft
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Pages
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredManuals.map((manual) => (
                <tr key={manual.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy-900/10">
                        <FileText className="h-5 w-5 text-navy-900" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{manual.name}</p>
                        <p className="text-sm text-gray-500">{manual.revision} • {manual.size}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <Badge variant="outline">{manual.type}</Badge>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-gray-900">{manual.aircraft}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-gray-900">{manual.pages.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-4">
                    {getStatusBadge(manual.status)}
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-gray-500">{formatDate(manual.uploadedAt)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Eye className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Re-index
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Upload Manual</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseModal}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Success Message */}
            {uploadSuccess && (
              <div className="mb-4 rounded-md bg-green-50 p-4 text-green-700 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                <span>Manual uploaded successfully! Processing will begin shortly.</span>
              </div>
            )}

            {/* Error Message */}
            {uploadError && (
              <div className="mb-4 rounded-md bg-red-50 p-4 text-red-700 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                <span>{uploadError}</span>
              </div>
            )}

            {/* Drag & Drop Zone */}
            <div
              className={cn(
                'rounded-lg border-2 border-dashed p-8 text-center transition-colors',
                dragActive
                  ? 'border-voltaire-blue bg-blue-50'
                  : selectedFile
                  ? 'border-green-400 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              )}
              onDragOver={(e) => {
                e.preventDefault()
                setDragActive(true)
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleFileDrop}
            >
              {selectedFile ? (
                <>
                  <FileText className="mx-auto h-12 w-12 text-green-500" />
                  <p className="mt-4 text-lg font-medium text-gray-900">
                    {selectedFile.name}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setSelectedFile(null)}
                  >
                    Remove File
                  </Button>
                </>
              ) : (
                <>
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-4 text-lg font-medium text-gray-900">
                    Drop your PDF here
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    or click to browse files
                  </p>
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    id="file-upload"
                    onChange={handleFileInputChange}
                  />
                  <Button variant="outline" className="mt-4" asChild>
                    <label htmlFor="file-upload" className="cursor-pointer">
                      Select File
                    </label>
                  </Button>
                  <p className="mt-4 text-xs text-gray-400">
                    Maximum file size: 200 MB • PDF files only
                  </p>
                </>
              )}
            </div>

            {/* Manual metadata form */}
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Document Type
                </label>
                <select className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <option>Aircraft Maintenance Manual (AMM)</option>
                  <option>Component Maintenance Manual (CMM)</option>
                  <option>Fault Isolation Manual (FIM)</option>
                  <option>Service Bulletin (SB)</option>
                  <option>Airworthiness Directive (AD)</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Aircraft Type
                </label>
                <select className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <option>B737-800</option>
                  <option>B737-MAX</option>
                  <option>A320</option>
                  <option>A321</option>
                  <option>B777-300ER</option>
                  <option>B787-9</option>
                  <option>Other</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={handleCloseModal} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={uploading || !selectedFile}>
                {uploading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload & Process
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
